import { useMemo } from 'react';
import {
    calculateEmailAnalytics,
    getFlowScheduledTime,
    getTriggerText,
    shouldBlockAddingEmail,
} from '../FlowEmailTimeline.logic';
import type { FlowEmail, FlowSummary, FlowTrigger } from '@/lib/api/email-flows';
import type { EmailAnalytics } from '../FlowEmailTimeline.types';

interface UseFlowEmailTimelineParams {
    flow: FlowSummary;
    flowTrigger: FlowTrigger;
    flowEmails: FlowEmail[];
    hasNewReferralTag: boolean;
    currency: string;
    compact?: boolean;
}

interface UseFlowEmailTimelineReturn {
    triggerText: string;
    blockAddingEmail: boolean;
    emailAnalytics: Record<number, EmailAnalytics>;
}

export const useFlowEmailTimeline = ({
    flow,
    flowTrigger,
    flowEmails,
    hasNewReferralTag,
    currency,
    compact = false,
}: UseFlowEmailTimelineParams): UseFlowEmailTimelineReturn => {
    const flowScheduledTime = getFlowScheduledTime(flow);
    const triggerText = getTriggerText(flowTrigger, flowScheduledTime, hasNewReferralTag, compact, flow);
    const blockAddingEmail = shouldBlockAddingEmail(flowTrigger, flowEmails.length, flow);

    const emailAnalytics = useMemo(() => {
        return flowEmails.reduce(
            (acc, email) => {
                acc[email.flow_email_id] = calculateEmailAnalytics(email, currency);
                return acc;
            },
            {} as Record<number, EmailAnalytics>
        );
    }, [flowEmails, currency]);

    return {
        triggerText,
        blockAddingEmail,
        emailAnalytics,
    };
};
